import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess } from '../App';
import { api } from '../lib/api';
import { Zap, MapPin, Calendar, CheckCircle } from 'lucide-react';

const PROVINCE_MAP = {
  'Ontario': 'ON', 'Quebec': 'QC', 'British Columbia': 'BC',
  'Alberta': 'AB', 'Manitoba': 'MB', 'Saskatchewan': 'SK',
  'Nova Scotia': 'NS', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Prince Edward Island': 'PE',
  'Northwest Territories': 'NT', 'Nunavut': 'NU', 'Yukon': 'YT',
};

function AddressAutocomplete({ onSelect, inputCls }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    if (val.length < 4) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=6&lang=en&countrycode=ca`;
        const r = await fetch(url);
        const data = await r.json();
        const items = (data.features || [])
          .filter(f => f.properties?.street || f.properties?.name)
          .slice(0, 5);
        setResults(items);
        setOpen(items.length > 0);
      } catch { /* ignore */ } finally { setLoading(false); }
    }, 320);
  };

  const pick = (feature) => {
    const p = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    const houseNum = p.housenumber ? `${p.housenumber} ` : '';
    const street = p.street || p.name || '';
    const address = `${houseNum}${street}`.trim();
    const city = p.city || p.town || p.village || p.county || '';
    const province = PROVINCE_MAP[p.state] || p.state || 'ON';
    const postcode = p.postcode || '';
    onSelect({ address, city, province, postcode, lat, lng });
    setQuery(address ? `${address}, ${city}` : city);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          className={`${inputCls} pl-9`}
          value={query}
          onChange={e => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search address…"
          autoComplete="off"
        />
        {loading && (
          <span className="absolute right-3 top-2.5 text-xs text-gray-400 animate-pulse">searching…</span>
        )}
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
          {results.map((f, i) => {
            const p = f.properties;
            const houseNum = p.housenumber ? `${p.housenumber} ` : '';
            const street = p.street || p.name || '';
            const city = p.city || p.town || p.village || p.county || '';
            const province = PROVINCE_MAP[p.state] || p.state || '';
            return (
              <li key={i}
                className="px-3 py-2.5 hover:bg-nova-50 cursor-pointer border-b last:border-0"
                onMouseDown={() => pick(f)}>
                <span className="font-medium">{houseNum}{street}</span>
                {city && <span className="text-gray-500"> — {city}{province ? `, ${province}` : ''}</span>}
                {p.postcode && <span className="text-gray-400 text-xs ml-1">{p.postcode}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function SaleEntry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [salesmen, setSalesmen] = useState([]);
  const [colors, setColors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dateSuggestions, setDateSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    customer_name: '',
    address: '',
    city: '',
    province: 'ON',
    lat: '',
    lng: '',
    phone: '',
    total_footage: '',
    power_boxes_350w: '',
    power_boxes_600w: '',
    channel_color_id: '',
    color_name: '',
    color_match_needed: false,
    deal_total: '',
    payment_type: 'cash',
    deposit_amount: '',
    remaining_after_install: '',
    finance_monthly_amount: '',
    finance_term_months: '',
    finance_after_install: '',   // lump sum paid after install before monthly starts
    finance_after_install_cash: false,  // if true: that lump sum is paid in cash → no HST on it
    finance_cc_fee_pct: '',
    finance_notes: '',
    ideal_install_date: '',
    install_window: '',
    flexible_schedule: false,
    notes: '',
    call_recording: '',
    salesman_id: user?.role === 'salesman' ? user?.id : '',
  });

  useEffect(() => {
    api.get('/inventory/channels').then(setColors);
    if (hasManagerAccess(user)) {
      api.get('/users/salesmen').then(setSalesmen);
    } else {
      setForm(f => ({ ...f, salesman_id: user.id }));
    }
  }, [user]);

  // E-Transfer / CC: remaining after install = deal total − deposit (auto)
  useEffect(() => {
    if (form.payment_type !== 'etransfer' && form.payment_type !== 'cc') return;
    const total   = Number(form.deal_total)    || 0;
    const deposit = Number(form.deposit_amount) || 0;
    const remaining = Math.max(0, total - deposit);
    setForm(f => ({ ...f, remaining_after_install: remaining || '' }));
  }, [form.payment_type, form.deal_total, form.deposit_amount]);

  // Financed: monthly = (deal total − deposit − after-install amount) ÷ term
  useEffect(() => {
    if (form.payment_type !== 'financed') return;
    const total        = Number(form.deal_total)          || 0;
    const deposit      = Number(form.deposit_amount)       || 0;
    const afterInstall = Number(form.finance_after_install) || 0;
    const term         = Number(form.finance_term_months)   || 0;
    if (term <= 0 || total <= 0) { setForm(f => ({ ...f, finance_monthly_amount: '' })); return; }
    const toFinance = Math.max(0, total - deposit - afterInstall);
    const monthly   = Math.round((toFinance / term) * 100) / 100;
    setForm(f => ({ ...f, finance_monthly_amount: monthly }));
  }, [form.payment_type, form.deal_total, form.deposit_amount, form.finance_after_install, form.finance_term_months]);

  const fetchSuggestions = () => {
    if (!form.city && !form.total_footage) return;
    setLoadingSuggestions(true);
    const params = new URLSearchParams();
    if (form.city)              params.set('city', form.city);
    if (form.total_footage)     params.set('footage', form.total_footage);
    if (form.ideal_install_date) params.set('ideal', form.ideal_install_date);
    api.get(`/schedule/suggest-date?${params}`)
      .then(setDateSuggestions)
      .catch(() => {})
      .finally(() => setLoadingSuggestions(false));
  };

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const handleColorChange = (e) => {
    const id = e.target.value;
    const color = colors.find(c => c.id === Number(id));
    setForm(f => ({ ...f, channel_color_id: id, color_name: color?.color_name || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const newErrors = {};
    if (!form.address.trim()) newErrors.address = 'Street address is required';
    if (!form.city.trim())    newErrors.city    = 'City is required';
    if (!(Number(form.deposit_amount) > 0)) newErrors.deposit_amount = 'Deposit amount is required';
    if (form.payment_type === 'cash') {
      if (!(Number(form.remaining_after_install) > 0)) newErrors.remaining_after_install = 'Remaining after install is required';
    } else if (form.payment_type === 'etransfer' || form.payment_type === 'cc') {
      // etransfer / cc — auto-calculated, must be > 0
      if (!(Number(form.remaining_after_install) > 0)) newErrors.remaining_after_install = 'Remaining after install must be > 0 (check deal total vs deposit)';
    }
    // financed: finance_after_install is optional (many clients do $0 after install)
    if (!form.channel_color_id && !form.color_match_needed) newErrors.color = 'Select a color or check "Needs color match"';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstField = document.querySelector('[data-error]');
      if (firstField) firstField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        deal_total: Number(form.deal_total) || 0,
        total_footage: Number(form.total_footage) || 0,
        power_boxes_350w: Number(form.power_boxes_350w) || 0,
        power_boxes_600w: Number(form.power_boxes_600w) || 0,
        deposit_amount: Number(form.deposit_amount) || 0,
        // For financed, remaining_after_install = lump sum paid after install
        remaining_after_install: form.payment_type === 'financed'
          ? (Number(form.finance_after_install) || null)
          : (Number(form.remaining_after_install) || null),
        after_install_cash: form.payment_type === 'financed' && form.finance_after_install_cash ? 1 : 0,
        finance_monthly_amount: Number(form.finance_monthly_amount) || null,
        finance_term_months: Number(form.finance_term_months) || null,
        finance_cc_fee_pct: Number(form.finance_cc_fee_pct) || null,
        channel_color_id: Number(form.channel_color_id) || null,
        salesman_id: Number(form.salesman_id) || user.id,
      };
      const result = await api.post('/sales', payload);
      navigate(`/sales/${result.id}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm";
  const inputErr = "w-full px-3 py-2 border-2 border-red-400 rounded-lg focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none text-sm";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const errMsg   = (key) => errors[key] ? <p className="text-xs text-red-500 mt-1 font-medium">{errors[key]}</p> : null;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Zap className="w-6 h-6 text-nova-600" />
        <h1 className="text-2xl font-bold">New Sale</h1>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
          <span className="text-red-500 font-bold text-sm shrink-0">!</span>
          <div>
            <p className="text-sm font-semibold text-red-700 mb-1">Please fix the following before submitting:</p>
            <ul className="text-sm text-red-600 list-disc list-inside space-y-0.5">
              {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Customer Info */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Customer Name *</label>
              <input className={inputCls} value={form.customer_name} onChange={set('customer_name')} required />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input className={inputCls} value={form.phone} onChange={set('phone')} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>
                Address
                {form.lat && form.lng && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600 font-normal">
                    <CheckCircle className="w-3 h-3" /> GPS captured
                  </span>
                )}
              </label>
              <AddressAutocomplete
                inputCls={inputCls}
                onSelect={({ address, city, province, lat, lng }) => {
                  setForm(f => ({ ...f, address, city, province, lat, lng }));
                }}
              />
              <p className="text-xs text-gray-400 mt-1">Search to auto-fill, or fill manually below</p>
            </div>
            <div className="md:col-span-2" data-error={errors.address ? 'address' : undefined}>
              <label className={labelCls}>Street Address <span className="text-red-500">*</span></label>
              <input className={errors.address ? inputErr : inputCls} value={form.address} onChange={e => {
                setForm(f => ({ ...f, address: e.target.value, lat: '', lng: '' }));
                if (errors.address) setErrors(p => ({ ...p, address: undefined }));
              }} placeholder="Street address" />
              {errMsg('address')}
            </div>
            <div data-error={errors.city ? 'city' : undefined}>
              <label className={labelCls}>City <span className="text-red-500">*</span></label>
              <input className={errors.city ? inputErr : inputCls} value={form.city} onChange={e => {
                set('city')(e);
                if (errors.city) setErrors(p => ({ ...p, city: undefined }));
              }} />
              {errMsg('city')}
            </div>
            <div>
              <label className={labelCls}>Province</label>
              <input className={inputCls} value={form.province} onChange={set('province')} />
            </div>
          </div>
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-4">Job Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className={labelCls}>Total Footage *</label>
              <input type="number" className={inputCls} value={form.total_footage} onChange={set('total_footage')} placeholder="e.g. 92" />
            </div>

            <div>
              <label className={labelCls}>Power Boxes — 350W</label>
              <input
                type="number" min="0" className={inputCls}
                value={form.power_boxes_350w}
                onChange={set('power_boxes_350w')}
                placeholder="0"
              />
              <p className="text-xs text-gray-400 mt-1">e.g. 2 if doing front &amp; back separately</p>
            </div>

            <div>
              <label className={labelCls}>Power Boxes — 600W</label>
              <input
                type="number" min="0" className={inputCls}
                value={form.power_boxes_600w}
                onChange={set('power_boxes_600w')}
                placeholder="0"
              />
            </div>

            <div data-error={errors.color ? 'color' : undefined}>
              <label className={labelCls}>Channel Color <span className="text-red-500">*</span></label>
              <select className={errors.color ? inputErr : inputCls} value={form.channel_color_id} onChange={e => {
                handleColorChange(e);
                if (errors.color) setErrors(p => ({ ...p, color: undefined }));
              }}>
                <option value="">Select color...</option>
                {colors.map(c => {
                  const eff = c.effective_available_feet ?? c.total_feet_in_stock;
                  const flag = c.status === 'critical' || c.status === 'out_of_stock';
                  return (
                    <option key={c.id} value={c.id}>
                      {c.color_name} — {eff}ft available{flag ? ' ⚠️' : ''}
                    </option>
                  );
                })}
              </select>
              {errMsg('color')}
            </div>

            <div className="flex items-center gap-2 self-end pb-1">
              <input type="checkbox" id="color_match" checked={form.color_match_needed} onChange={e => {
                set('color_match_needed')(e);
                if (errors.color) setErrors(p => ({ ...p, color: undefined }));
              }} className="w-4 h-4 rounded text-nova-600" />
              <label htmlFor="color_match" className="text-sm">Needs color match</label>
            </div>

            {/* Salesman — read-only for salesmen, dropdown for admin */}
            <div className="md:col-span-2">
              <label className={labelCls}>Salesman</label>
              {hasManagerAccess(user) ? (
                <select className={inputCls} value={form.salesman_id} onChange={set('salesman_id')}>
                  <option value="">Select salesman...</option>
                  {salesmen.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.full_name} ({(s.commission_rate * 100).toFixed(0)}% commission)
                    </option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 border rounded-lg bg-gray-50 text-sm text-gray-700 font-medium">
                  {user.full_name}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-4">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Deal Total *</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                <input type="number" className={`${inputCls} pl-7`} value={form.deal_total} onChange={set('deal_total')} required />
              </div>
            </div>
            <div>
              <label className={labelCls}>Payment Type *</label>
              <select className={inputCls} value={form.payment_type} onChange={set('payment_type')}>
                <option value="cash">Cash</option>
                <option value="etransfer">E-Transfer</option>
                <option value="cc">Credit Card</option>
                <option value="financed">Financed (In-House)</option>
              </select>
            </div>
            <div data-error={errors.deposit_amount ? 'deposit_amount' : undefined}>
              <label className={labelCls}>Deposit Amount <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                <input type="number" className={`${errors.deposit_amount ? inputErr : inputCls} pl-7`} value={form.deposit_amount} onChange={e => {
                  set('deposit_amount')(e);
                  if (errors.deposit_amount) setErrors(p => ({ ...p, deposit_amount: undefined }));
                }} />
              </div>
              {errMsg('deposit_amount')}
            </div>
            {/* Cash: manual remaining after install */}
            {form.payment_type === 'cash' && (
              <div data-error={errors.remaining_after_install ? 'remaining_after_install' : undefined}>
                <label className={labelCls}>Remaining After Install <span className="text-red-500">*</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                  <input type="number" className={`${errors.remaining_after_install ? inputErr : inputCls} pl-7`} value={form.remaining_after_install} onChange={e => {
                    set('remaining_after_install')(e);
                    if (errors.remaining_after_install) setErrors(p => ({ ...p, remaining_after_install: undefined }));
                  }} />
                </div>
                {errMsg('remaining_after_install')}
              </div>
            )}

            {/* E-Transfer / CC: auto-calculated remaining */}
            {(form.payment_type === 'etransfer' || form.payment_type === 'cc') && (
              <div data-error={errors.remaining_after_install ? 'remaining_after_install' : undefined}>
                <label className={labelCls}>
                  Remaining After Install
                  <span className="ml-1.5 text-xs font-normal text-blue-500">auto-calculated</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                  <input type="number" className={`${errors.remaining_after_install ? inputErr : inputCls} pl-7 bg-blue-50 text-blue-800 font-medium`}
                    value={form.remaining_after_install} readOnly />
                </div>
                {errors.remaining_after_install
                  ? errMsg('remaining_after_install')
                  : <p className="text-xs text-gray-400 mt-1">Deal total − deposit</p>
                }
              </div>
            )}

            {/* Financed fields */}
            {form.payment_type === 'financed' && (
              <>
                <div>
                  <label className={labelCls}>Term (months)</label>
                  <div className="flex gap-2">
                    <div className="flex rounded-lg border overflow-hidden text-sm shrink-0">
                      {[6, 12, 18, 24].map(m => (
                        <button key={m} type="button"
                          onClick={() => setForm(f => ({ ...f, finance_term_months: String(m) }))}
                          className={`px-3 py-2 font-medium transition-colors ${
                            Number(form.finance_term_months) === m
                              ? 'bg-nova-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <input type="number" min="1" className={inputCls} value={form.finance_term_months}
                      onChange={set('finance_term_months')} placeholder="or type…" />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    Amount Paid After Install
                    <span className="ml-1.5 text-xs font-normal text-gray-400">optional</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                    <input type="number" className={`${inputCls} pl-7`} value={form.finance_after_install}
                      onChange={set('finance_after_install')} placeholder="0" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Lump sum paid at install before monthly billing starts</p>
                  {Number(form.finance_after_install) > 0 && (
                    <label className="mt-2 flex items-center gap-2 text-xs bg-green-50 border border-green-200 rounded-lg px-3 py-2 cursor-pointer">
                      <input type="checkbox" checked={form.finance_after_install_cash}
                        onChange={set('finance_after_install_cash')}
                        className="w-3.5 h-3.5 rounded text-green-600 focus:ring-green-500" />
                      <span className="text-green-800 font-medium">After-install paid in CASH (no HST)</span>
                      <span className="text-green-600">— only the financed portion is taxed</span>
                    </label>
                  )}
                </div>

                <div>
                  <label className={labelCls}>
                    Monthly Payment
                    <span className="ml-1.5 text-xs font-normal text-blue-500">auto-calculated</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                    <input type="number" className={`${inputCls} pl-7 bg-blue-50 text-blue-800 font-medium`}
                      value={form.finance_monthly_amount} readOnly
                      placeholder={form.finance_term_months ? 'Enter deal total & deposit' : 'Select a term first'} />
                  </div>
                  {form.finance_monthly_amount > 0 && form.finance_term_months && (
                    <p className="text-xs text-gray-400 mt-1">
                      ${(Number(form.deal_total) - Number(form.deposit_amount) - (Number(form.finance_after_install) || 0)).toFixed(2)} financed ÷ {form.finance_term_months} months
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>CC Fee %</label>
                  <input type="number" step="0.1" className={inputCls} value={form.finance_cc_fee_pct}
                    onChange={set('finance_cc_fee_pct')} placeholder="e.g. 3" />
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>Finance Notes</label>
                  <input className={inputCls} value={form.finance_notes} onChange={set('finance_notes')}
                    placeholder="e.g. included the 3% CC fee, client on Rotessa PAD" />
                </div>
              </>
            )}

            {form.payment_type === 'cash' ? (() => {
              const total = Number(form.deal_total) || 0;
              const deposit = Number(form.deposit_amount) || 0;
              const taxOnDeposit = deposit * (0.13 / 1.13);
              const commissionBase = total - taxOnDeposit;
              return (
                <p className="text-xs text-gray-500 md:col-span-2">
                  Cash: HST on deposit only (${taxOnDeposit.toFixed(2)}). Commission base: ${commissionBase.toFixed(2)}
                </p>
              );
            })() : (() => {
              const total = Number(form.deal_total) || 0;
              const cashPortion = form.payment_type === 'financed' && form.finance_after_install_cash
                ? Math.max(0, Number(form.finance_after_install) || 0)
                : 0;
              const taxable = Math.max(0, total - cashPortion);
              const taxablePreTax = taxable / 1.13;
              const tax = taxable - taxablePreTax;
              const preTax = taxablePreTax + cashPortion;
              if (cashPortion > 0) {
                return (
                  <p className="text-xs text-gray-500 md:col-span-2">
                    Financed (cash after-install): HST only on ${taxable.toFixed(2)} (tax ${tax.toFixed(2)}).
                    Cash portion ${cashPortion.toFixed(2)} untaxed. Pre-tax: ${preTax.toFixed(2)}
                  </p>
                );
              }
              return (
                <p className="text-xs text-gray-500 md:col-span-2">
                  Non-cash: 13% HST on full amount. Pre-tax: ${(total / 1.13).toFixed(2)}
                </p>
              );
            })()}

            {/* Tiered commission preview (e.g. GEO) */}
            {(() => {
              const brk = Number(user?.commission_tier_break_per_ft) || 0;
              const t1  = Number(user?.commission_tier1_rate) || 0;
              const t2  = Number(user?.commission_tier2_rate) || 0;
              const mt1 = Number(user?.manager_tier1_cut) || 0;
              const total = Number(form.deal_total) || 0;
              const footage = Number(form.total_footage) || 0;
              const deposit = Number(form.deposit_amount) || 0;
              if (!brk || !footage || !total) return null;

              // Pre-tax matches backend calcTax
              let preTax;
              if (form.payment_type === 'cash') {
                const taxOnDeposit = deposit * (0.13 / 1.13);
                preTax = total - taxOnDeposit;
              } else {
                preTax = total / 1.13;
              }
              const perFt = preTax / footage;
              const tier1Base = Math.min(preTax, brk * footage);
              const tier2Base = Math.max(0, preTax - tier1Base);
              const geo1 = tier1Base * t1;
              const geo2 = tier2Base * t2;
              const mgr1 = tier1Base * mt1;
              const geoTotal = geo1 + geo2;
              const fmt = n => '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

              return (
                <div className="md:col-span-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mt-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-green-800">Your Tiered Commission</p>
                    <span className="text-xs text-green-600 font-medium">
                      ${perFt.toFixed(2)}/ft pre-tax · break at ${brk}/ft
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">Tier 1 (≤ ${brk}/ft)</p>
                      <p className="font-semibold text-gray-800">{fmt(tier1Base)}</p>
                      <p className="text-green-600">× {(t1*100).toFixed(0)}% = {fmt(geo1)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Tier 2 (&gt; ${brk}/ft)</p>
                      <p className="font-semibold text-gray-800">{fmt(tier2Base)}</p>
                      <p className="text-green-600">× {(t2*100).toFixed(0)}% = {fmt(geo2)}</p>
                    </div>
                    <div className="md:border-l md:pl-3">
                      <p className="text-gray-500">You earn</p>
                      <p className="text-lg font-bold text-green-700">{fmt(geoTotal)}</p>
                    </div>
                    {mt1 > 0 && user?.role === 'admin' && (
                      <div>
                        <p className="text-gray-500">Manager cut</p>
                        <p className="text-sm font-semibold text-blue-600">{fmt(mgr1)}</p>
                        <p className="text-gray-400">{(mt1*100).toFixed(0)}% on tier 1</p>
                      </div>
                    )}
                  </div>
                  {tier2Base === 0 && (
                    <p className="text-xs text-amber-600 mt-2 italic">
                      💡 Push above ${brk}/ft to unlock the higher {(t2*100).toFixed(0)}% tier.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Install Timing */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Install Timing</h2>
            <button type="button" onClick={fetchSuggestions} disabled={loadingSuggestions || (!form.city && !form.total_footage)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-nova-50 text-nova-700 border border-nova-200 rounded-lg hover:bg-nova-100 disabled:opacity-40 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              {loadingSuggestions ? 'Finding dates…' : 'Suggest Dates'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Preferred Install Date
                <span className="ml-1.5 text-xs font-normal text-gray-400">soft — confirmed later by management</span>
              </label>
              <input type="date" className={inputCls} value={form.ideal_install_date} onChange={e => {
                set('ideal_install_date')(e);
                setDateSuggestions(null);
              }} />
            </div>
            <div>
              <label className={labelCls}>Install Window</label>
              <input className={inputCls} value={form.install_window} onChange={set('install_window')} placeholder="e.g. early April, end of April" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="flexible" checked={form.flexible_schedule} onChange={set('flexible_schedule')}
                className="w-4 h-4 rounded text-nova-600" />
              <label htmlFor="flexible" className="text-sm">Flexible on date</label>
            </div>
          </div>

          {/* Suggestions */}
          {!form.city && !form.total_footage && !dateSuggestions && (
            <p className="mt-3 text-xs text-gray-400">Fill in city and footage above to get date suggestions based on existing routes.</p>
          )}

          {loadingSuggestions && (
            <p className="mt-3 text-xs text-gray-400 animate-pulse">Scanning schedule for best dates…</p>
          )}

          {dateSuggestions && dateSuggestions.suggestions.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-500 mb-2">
                Tap a date to use it as your preferred install date — management will confirm:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {dateSuggestions.suggestions.map(s => {
                  const [y, m, d] = s.date.split('-').map(Number);
                  const label = new Date(y, m-1, d).toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric' });
                  const isSelected = form.ideal_install_date === s.date;
                  const isIdeal    = s.type === 'ideal' || s.reasons.includes("Salesman's ideal date");
                  const isSameCity = s.same_city;
                  return (
                    <button key={s.date} type="button"
                      onClick={() => setForm(f => ({ ...f, ideal_install_date: s.date }))}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        isSelected   ? 'border-nova-500 bg-nova-50 ring-2 ring-nova-200' :
                        isIdeal      ? 'border-nova-300 bg-nova-50 hover:border-nova-400' :
                        isSameCity   ? 'border-green-300 bg-green-50 hover:border-green-400' :
                        'border-gray-200 bg-white hover:border-gray-300'
                      }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm">{label}</span>
                        {isSelected && <span className="text-[10px] bg-nova-500 text-white px-1.5 py-0.5 rounded-full">Selected</span>}
                        {!isSelected && isSameCity && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Route match</span>}
                      </div>
                      {s.cities.length > 0 && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" />{s.cities.join(', ')}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {s.existing_footage > 0 ? `${s.existing_footage}ft already booked` : 'Open day'}
                      </p>
                      {s.reasons[0] && <p className="text-[11px] text-nova-600 mt-1 font-medium">{s.reasons[0]}</p>}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> This sets a <strong>soft preferred date</strong>. The manager will confirm the actual installation schedule.
              </p>
            </div>
          )}

          {dateSuggestions && dateSuggestions.suggestions.length === 0 && (
            <p className="mt-3 text-xs text-gray-400">No route matches found — enter a preferred date manually above.</p>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-4">Notes</h2>
          <textarea className={`${inputCls} h-24`} value={form.notes} onChange={set('notes')}
            placeholder="Contact instructions, special requests, color match notes..." />
        </div>

        {/* Call Recording */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-1">Call Recording</h2>
          <p className="text-xs text-gray-400 mb-3">Paste a link to the call recording for this intake</p>
          <input className={inputCls} value={form.call_recording} onChange={set('call_recording')}
            placeholder="https://..." type="url" />
        </div>

        <button type="submit" disabled={submitting}
          className="w-full bg-nova-600 text-white py-3 rounded-xl font-medium hover:bg-nova-700 disabled:opacity-50 transition-colors text-lg">
          {submitting ? 'Submitting...' : 'Submit Sale'}
        </button>
      </form>
    </div>
  );
}
